import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory } from 'react-router-dom';

import * as Styled from './styles';

const matchesPath = (pathname, path) => {
  if (!path) return false;
  if (pathname === path) return true;
  return pathname.startsWith(path === '/' ? path : `${path}/`);
};

const SubPagesPopup = ({ section, currentPathname, onClose, placement }) => {
  const history = useHistory();
  const containerRef = useRef(null);

  const subRoutes = useMemo(() => (Array.isArray(section.subRoutes) ? section.subRoutes : []), [section.subRoutes]);

  const currentIndex = useMemo(
    () => subRoutes.findIndex((sub) => matchesPath(currentPathname, sub.path)),
    [subRoutes, currentPathname],
  );

  const [activeIndex, setActiveIndex] = useState(currentIndex >= 0 ? currentIndex : 0);

  const selectSubRoute = useCallback(
    (subRoute) => {
      if (!subRoute) return;
      if (subRoute.href) {
        window.open(subRoute.href, subRoute.href.startsWith('mailto:') ? '_self' : '_blank');
      } else if (subRoute.path) {
        history.push(subRoute.path);
      }
      onClose();
    },
    [history, onClose],
  );

  // Items are real links, so let the browser handle anything that isn't a plain left-click —
  // cmd/ctrl-click, shift-click and middle-click must open a new tab/window rather than navigating this
  // one. Right-click never reaches here at all; it gets the native context menu because there is an href.
  const handleItemClick = useCallback(
    (e, subRoute) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
        onClose();
        return;
      }
      e.preventDefault();
      selectSubRoute(subRoute);
    },
    [selectSubRoute, onClose],
  );

  // Middle-click opens a new tab, so dismiss the popup afterwards. Guarded on button 1: `auxclick` fires
  // for every non-primary button, so an unguarded handler here also caught RIGHT-click and closed the
  // popup before the context menu could be used.
  const handleAuxClick = useCallback(
    (e) => {
      if (e.button === 1) onClose();
    },
    [onClose],
  );

  // Internal routes render as <Link> (href + SPA navigation); external ones as a plain anchor.
  const linkProps = (subRoute) => {
    if (subRoute.href) {
      const external = !subRoute.href.startsWith('mailto:');
      return {
        as: 'a',
        href: subRoute.href,
        ...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {}),
      };
    }
    return subRoute.path ? { as: Link, to: subRoute.path } : { as: 'span' };
  };

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % subRoutes.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prev) => (prev - 1 + subRoutes.length) % subRoutes.length);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        selectSubRoute(subRoutes[activeIndex]);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [subRoutes, activeIndex, selectSubRoute, onClose]);

  return (
    <Styled.PopupContainer
      ref={containerRef}
      role="menu"
      tabIndex={-1}
      aria-label={`${section.title} sub-pages`}
      $placement={placement}
    >
      <Styled.Tail aria-hidden $placement={placement} />
      <Styled.Header>
        <Styled.Title>{section.title}</Styled.Title>
        <Styled.Count>{subRoutes.length}</Styled.Count>
      </Styled.Header>
      <Styled.Subtitle>Nested pages</Styled.Subtitle>
      <Styled.Divider />
      <Styled.List>
        {subRoutes.map((sub, index) => {
          const isCurrent = matchesPath(currentPathname, sub.path);
          const isActive = index === activeIndex;
          return (
            <Styled.Item
              key={sub.id ?? sub.path ?? sub.title}
              {...linkProps(sub)}
              role="menuitem"
              tabIndex={-1}
              aria-current={isCurrent ? 'page' : undefined}
              $current={isCurrent}
              $active={isActive}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={(e) => handleItemClick(e, sub)}
              onAuxClick={handleAuxClick}
            >
              {isCurrent ? <Styled.CurrentDot aria-hidden /> : <span style={{ width: 6 }} />}
              <Styled.ItemLabel>{sub.title}</Styled.ItemLabel>
            </Styled.Item>
          );
        })}
      </Styled.List>
      <Styled.Footer>
        <Styled.Hint>
          <Styled.Kbd>↑↓</Styled.Kbd> select
        </Styled.Hint>
        <Styled.Hint>
          <Styled.Kbd>↵</Styled.Kbd> open
        </Styled.Hint>
        <Styled.Hint>
          <Styled.Kbd>esc</Styled.Kbd> close
        </Styled.Hint>
      </Styled.Footer>
    </Styled.PopupContainer>
  );
};

SubPagesPopup.propTypes = {
  section: PropTypes.shape({
    title: PropTypes.string,
    subRoutes: PropTypes.array,
  }).isRequired,
  currentPathname: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  placement: PropTypes.oneOf(['right', 'below']),
};

SubPagesPopup.defaultProps = {
  placement: 'right',
};

export default SubPagesPopup;
